import { useRef, useState, type CSSProperties, type FormEvent } from "react";
import type { ValidatePhone } from "@/entity/login";
import { loginService } from "@/api/services/loginService";
import { useUserStore } from "@/views/stores";
import { useWxLogin as createWxLoginAdapter } from "@/views/login/useWxLogin";
import { clearEnterpriseWeChatCallback, loginWithEnterpriseWeChatCode } from "@/views/auth/enterpriseWeChat";
import logoUrl from "@/views/assets/images/logo.svg";
import policeInsigniaUrl from "@/views/assets/images/police-insignia.png";

const loginVisualUrl =
    "https://zhihaoscm-public.oss-cn-hangzhou.aliyuncs.com/customer-admin/lian-yun-sha-shi-admin-login-2026-01-30.webp";
const loginBackgroundUrl = "https://static.zhihaoscm.cn/admin/login/zhihao.png";

export interface EnterpriseWeChatLoginParams {
    code: string;
    source: "web";
    url: "/";
}

export interface LoginPageProps {
    onSubmit?: (values: ValidatePhone) => Promise<void>;
    /** 企业微信登录面板返回临时 code 的适配器，便于测试替换真实 SDK。 */
    createEnterpriseWeChatLogin?: () => Promise<string>;
    /** 企业微信配置开关；未传入时沿用 Vite 环境变量判断。 */
    enterpriseWeChatConfigured?: boolean;
    /** 登录成功后的首页跳转边界，默认使用浏览器首页跳转。 */
    onNavigateHome?: (path: "/") => void;
}

/** 管理后台登录页，保留手机号登录并补齐 Vue 版本企业微信登录入口。 */
export function LoginPage({
    onSubmit = defaultSubmit,
    createEnterpriseWeChatLogin,
    enterpriseWeChatConfigured,
    onNavigateHome
}: LoginPageProps) {
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [enterpriseSubmitting, setEnterpriseSubmitting] = useState(false);
    const wxLoginAdapter = useRef<ReturnType<typeof createWxLoginAdapter> | null>(null);

    // 真实环境要求同时配置企业 ID 和应用 ID；测试可显式注入开关与 SDK 适配器。
    const isEnterpriseWeChatConfigured =
        enterpriseWeChatConfigured ?? Boolean(import.meta.env.VITE_APP_ID && import.meta.env.VITE_AGENT_ID);

    /** 完成手动企微登录，复用旧版 Vue 的 code 换取 Token 逻辑。 */
    const completeEnterpriseWeChatLogin = async (loginCode: string) => {
        setError("");
        setEnterpriseSubmitting(true);
        try {
            await loginWithEnterpriseWeChatCode(loginCode);
            clearEnterpriseWeChatCallback();
            (onNavigateHome ?? defaultNavigateHome)("/");
        } catch (loginError) {
            setError(getEnterpriseWeChatErrorMessage(loginError));
        } finally {
            // 面板只负责获取一次 code，完成或失败后均释放，避免重复打开残留 iframe。
            wxLoginAdapter.current?.unmountLoginPanel();
            setEnterpriseSubmitting(false);
        }
    };

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await onSubmit({ phone, code: Number(code) });
        } catch (submitError) {
            setError(submitError instanceof Error ? submitError.message : "登录失败");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEnterpriseWeChatLogin = async () => {
        setError("");
        if (!isEnterpriseWeChatConfigured) {
            setError("企业微信登录暂未配置");
            return;
        }

        setEnterpriseSubmitting(true);
        try {
            // 延迟创建登录面板，避免页面初次渲染时触发企业微信 SDK 和外部请求。
            if (!wxLoginAdapter.current) {
                wxLoginAdapter.current = createWxLoginAdapter();
            }
            const loginCode = await (
                createEnterpriseWeChatLogin ?? (() => wxLoginAdapter.current!.createLoginPanel())
            )();
            await completeEnterpriseWeChatLogin(loginCode);
        } catch (loginError) {
            // 登录面板初始化失败时仍保留可见错误，避免用户只能看到按钮恢复原状。
            setError(getEnterpriseWeChatErrorMessage(loginError));
        } finally {
            setEnterpriseSubmitting(false);
        }
    };

    return (
        <main
            className="login-container"
            style={
                {
                    "--login-visual-url": `url(${loginVisualUrl})`,
                    "--login-background-url": `url(${loginBackgroundUrl})`
                } as CSSProperties
            }
        >
            <div className="login-container-left" aria-hidden="true" />
            <div className="login-container-right">
                <div className="company-info">
                    <img className="company-logo" src={logoUrl} alt="" />
                    <div className="company-copy">
                        <div className="company-name">志豪链云</div>
                        <div className="company-mission">产业新质生产力服务商</div>
                    </div>
                </div>
                <div className="login-panel">
                    <h1 className="login-panel-title">登录</h1>
                    <form className="login-form" onSubmit={submit}>
                        <label className="login-field">
                            手机号
                            <input
                                aria-label="手机号"
                                placeholder="请输入手机号"
                                value={phone}
                                onChange={event => setPhone(event.target.value)}
                            />
                        </label>
                        <label className="login-field">
                            验证码
                            <input
                                aria-label="验证码"
                                placeholder="请输入验证码"
                                value={code}
                                onChange={event => setCode(event.target.value)}
                            />
                        </label>
                        <button className="login-btn" type="submit" disabled={submitting}>
                            {submitting ? "登录中" : "登录"}
                        </button>
                    </form>
                    <section className="enterprise-login" aria-labelledby="enterprise-wechat-login-title">
                        <h2 id="enterprise-wechat-login-title">其他登录方式</h2>
                        <div id="ww_login" aria-hidden="true" />
                        <button
                            className="enterprise-login-btn"
                            type="button"
                            onClick={handleEnterpriseWeChatLogin}
                            disabled={enterpriseSubmitting}
                        >
                            {enterpriseSubmitting ? "企业微信登录中" : "企业微信登录"}
                        </button>
                    </section>
                    {error ? (
                        <p className="login-error" role="alert">
                            {error}
                        </p>
                    ) : null}
                </div>
                <footer className="login-footer">
                    <div className="copyright">
                        Copyright © 2024-{new Date().getFullYear()} 志豪链云科技有限公司 All Rights Reserved
                    </div>
                    <div className="filings">
                        <span>鄂B2-20240693</span>
                        <img src={policeInsigniaUrl} alt="" />
                        <span>鄂公网安备42110002000192号</span>
                    </div>
                </footer>
            </div>
        </main>
    );
}

async function defaultSubmit(values: ValidatePhone) {
    const result = await loginService.loginPhone(values);
    useUserStore.getState().setToken(result.accessToken);
}

/** 默认首页跳转保持登录入口的边界，不额外改变路由配置。 */
function defaultNavigateHome(path: "/") {
    window.location.assign(path);
}

/** 优先展示后端返回的可读错误码，其他错误沿用 Vue 登录页提示。 */
function getEnterpriseWeChatErrorMessage(error: unknown) {
    const responseCode = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
    if (responseCode !== undefined && responseCode !== null && Number.isNaN(Number(responseCode))) {
        return String(responseCode);
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return "获取企业微信登录错误，请刷新后再试";
}

export default LoginPage;
